import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WebIcon from "@mui/icons-material/Web";
import CategoryIcon from "@mui/icons-material/Category";
import LayersIcon from "@mui/icons-material/Layers";
import PaymentIcon from "@mui/icons-material/Payment";
import { resolveBriefingPlatformIcon } from "../../utils/iconUtils";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUserRole } from "../../hooks/useUserRole";

import Sidebar from "../../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import {
  useDeleteAssociatedCampaignGoalMutation,
  useDeleteAssociatedCampaignTargetMutation,
  useGetAssociatedCampaignQuery,
  useGetBriefingByIdQuery,
} from "../../store/apis/briefingApi";
import GoalDialog, {
  GoalFormState,
} from "../../components/briefings/GoalDialog";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { canEditAssetSection } from "../../utils/briefingPermissions";
import { components } from "../../types/api";
import { setToastMessage } from "../../store/slices/app/appSlice";
import AssociatedCampaignGoalSection from "../../components/briefings/AssociatedCampaignGoalSection";
import AssociatedCampaignTargetSection from "../../components/briefings/AssociatedCampaignTargetSection";
import AssociatedCampaignAssetSection from "../../components/briefings/AssociatedCampaignAssetSection";
import EditAcInfoDialog from "../../components/briefings/EditAcInfoDialog";
import ManageTargetDialog from "../../components/briefings/ManageTargetDialog";
import AssociatedCampaignAssetDialog from "../../components/briefings/AssociatedCampaignAssetDialog";
import AssociatedCampaignSyncSection from "../../components/briefings/AssociatedCampaignSyncSection";
import AssociatedCampaignMarginSection from "../../components/briefings/AssociatedCampaignMarginSection";
import AssociatedCampaignSyncDialog from "../../components/briefings/AssociatedCampaignSyncDialog";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type Role = components["schemas"]["Role"];

type ApiErrorWithMessage = { data?: { message?: string } };

const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as ApiErrorWithMessage).data?.message === "string"
  ) {
    return (error as ApiErrorWithMessage).data?.message;
  }
  return undefined;
};

const allowedViewRoles = ["ADMIN", "ACCOUNT", "TECHNICIAN", "PRICING"] as const;

const ViewAssociatedCampaign: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams<{
    id: string;
    associatedId: string;
  }>();

  const briefingId = params.id?.replace(/['"]/g, "");
  const associatedId = params.associatedId?.replace(/['"]/g, "");
  const collapsed = useSelector(selectSidebarCollapsed);
  const dispatch = useDispatch();

  const { canEditEverything, isAccount, role, canAccessControlAndMargin } =
    useUserRole();

  const canView = role
    ? allowedViewRoles.includes(role as (typeof allowedViewRoles)[number])
    : false;

  const {
    data,
    isFetching,
    isLoading,
    error,
    refetch: refetchAssociatedCampaign,
  } = useGetAssociatedCampaignQuery(
    {
      briefingId: briefingId ?? "",
      associatedId: associatedId ?? "",
    },
    { skip: !canView || !briefingId || !associatedId }
  );

  const { data: briefingResponse } = useGetBriefingByIdQuery(
    { id: briefingId ?? "" },
    { skip: !briefingId }
  );
  const companyName = briefingResponse?.data?.companyName || "";
  const companyId = briefingResponse?.data?.companyId || "";
  const campaignType = data?.data?.briefingCampaignType;
  const isBeApoloCompany =
    briefingResponse?.data?.companyName?.toLowerCase().includes("beapolo") ??
    false;

  // Dialog states
  const [isAcInfoDialogOpen, setAcInfoDialogOpen] = useState(false);
  const [isGoalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalDialogMode, setGoalDialogMode] = useState<"create" | "edit">(
    "create"
  );
  const [goalInitialValues, setGoalInitialValues] =
    useState<Partial<GoalFormState> | null>(null);

  const [isDeleteGoalModalOpen, setDeleteGoalModalOpen] = useState(false);
  const [isTargetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetDialogMode, setTargetDialogMode] = useState<"create" | "edit">(
    "create"
  );
  const [isDeleteTargetModalOpen, setDeleteTargetModalOpen] = useState(false);
  const [isAssetDialogOpen, setAssetDialogOpen] = useState(false);
  const [isSyncDialogOpen, setSyncDialogOpen] = useState(false);

  const [deleteGoal, { isLoading: isDeletingGoal }] =
    useDeleteAssociatedCampaignGoalMutation();
  const [deleteTarget, { isLoading: isDeletingTarget }] =
    useDeleteAssociatedCampaignTargetMutation();

  const associatedCampaign = data?.data;
  const goal = associatedCampaign?.goal ?? null;
  const target = associatedCampaign?.target ?? null;
  const asset = associatedCampaign?.asset ?? null;

  const campaignName = briefingResponse?.data?.campaignName || "";
  const brandOrProduct = briefingResponse?.data?.brandOrProduct || "";

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      }),
    []
  );

  // Deep-link a la sección de Margin (#margin), p. ej. desde /wip.
  const location = useLocation();
  useEffect(() => {
    if (location.hash !== "#margin") return;
    if (isLoading || isFetching || !canAccessControlAndMargin) return;
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById("margin");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts < 10) {
        attempts += 1;
        setTimeout(tryScroll, 150);
      }
    };
    tryScroll();
  }, [location.hash, isLoading, isFetching, canAccessControlAndMargin]);

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const handleBack = () => {
    if (briefingId) {
      navigate(`/briefings/${briefingId}`);
    } else {
      navigate("/briefings");
    }
  };

  const handleDeleteGoal = async (): Promise<void> => {
    if (!briefingId || !associatedId) return;
    try {
      await deleteGoal({ briefingId, associatedId }).unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.goalDeleteSuccess",
            "Goal deleted successfully"
          ),
          severity: "success",
        })
      );
      setDeleteGoalModalOpen(false);
      refetchAssociatedCampaign();
    } catch (error) {
      const apiMessage = extractApiErrorMessage(error);
      dispatch(
        setToastMessage({
          message:
            apiMessage ??
            t("briefingsView.goalDeleteError", "Failed to delete goal"),
          severity: "error",
        })
      );
    }
  };

  const handleCloseGoalDialog = (): void => {
    setGoalDialogOpen(false);
    setGoalInitialValues(null);
  };

  const handleGoalActionSuccess = (): void => {
    refetchAssociatedCampaign();
  };

  const handleOpenCreateGoal = (): void => {
    if (!briefingId || !associatedId) return;
    setGoalDialogMode("create");

    // Extract funnel from campaign name
    const funnelRegex = /(AWARENESS|CONSIDERATION|PERFORMANCE)/;
    const funnelMatch = associatedCampaign?.name?.match(funnelRegex);
    const funnel = funnelMatch
      ? (funnelMatch[1] as components["schemas"]["_36_Enums.BriefingFunnel"])
      : undefined;

    // Map funnel to default objective (KPI)
    let objective: components["schemas"]["BriefingObjective"] | undefined;
    if (funnel === "AWARENESS") objective = "CPM";
    else if (funnel === "CONSIDERATION") objective = "CPC";
    else if (funnel === "PERFORMANCE") objective = "CPA";

    const initialValues: Partial<GoalFormState> = {};
    if (funnel) initialValues.funnel = funnel;
    if (objective) initialValues.objective = objective;

    setGoalInitialValues(
      Object.keys(initialValues).length > 0 ? initialValues : null
    );
    setGoalDialogOpen(true);
  };

  const handleOpenEditGoal = (): void => {
    if (!briefingId || !associatedId || !associatedCampaign?.goal) return;
    setGoalDialogMode("edit");
    setGoalInitialValues(
      associatedCampaign.goal as unknown as Partial<GoalFormState>
    );
    setGoalDialogOpen(true);
  };

  const handleOpenDeleteGoal = (): void => {
    if (!briefingId || !associatedId) return;
    setDeleteGoalModalOpen(true);
  };

  const handleOpenCreateTarget = (): void => {
    if (!briefingId || !associatedId) return;
    setTargetDialogMode("create");
    setTargetDialogOpen(true);
  };

  const handleOpenEditTarget = (): void => {
    if (!briefingId || !associatedId || !target) return;
    setTargetDialogMode("edit");
    setTargetDialogOpen(true);
  };

  const handleDeleteTarget = async (): Promise<void> => {
    if (!briefingId || !associatedId) return;
    try {
      await deleteTarget({ briefingId, associatedId }).unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.targetDeleteSuccess",
            "Targeting details deleted successfully"
          ),
          severity: "success",
        })
      );
      setDeleteTargetModalOpen(false);
      refetchAssociatedCampaign();
    } catch (error) {
      const apiMessage = extractApiErrorMessage(error);
      dispatch(
        setToastMessage({
          message:
            apiMessage ??
            t(
              "briefingsView.targetDeleteError",
              "Failed to delete targeting details"
            ),
          severity: "error",
        })
      );
    }
  };

  const handleOpenCreateAsset = (): void => {
    if (!briefingId || !associatedId) return;
    setAssetDialogOpen(true);
  };

  const handleOpenEditAsset = (): void => {
    if (!briefingId || !associatedId || !asset) return;
    setAssetDialogOpen(true);
  };

  const canEditGoal = canEditEverything || isAccount;
  const canEditTarget = canEditEverything || isAccount;
  const canEditAsset =
    canEditEverything ||
    canEditAssetSection(role as Role, briefingResponse?.data?.status);

  const canEditAcInfo = canEditEverything || isAccount;
  const canEditSync = canEditEverything || isAccount;
  // El apartado Margin es visible/usable solo para ADMIN/STRATEGY/TECHNICIAN/PRICING.
  const canViewMargin = canAccessControlAndMargin;

  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <Stack direction="row" justifyContent="center" py={8}>
          <CircularProgress />
        </Stack>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          {t(
            "briefingsView.associatedCampaignError",
            "Unable to load associated campaign."
          )}
        </Alert>
      );
    }

    if (!associatedCampaign) {
      return (
        <Alert severity="warning">
          {t(
            "briefingsView.associatedCampaignNotFound",
            "Associated campaign not found."
          )}
        </Alert>
      );
    }

    return (
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 4 },
          }}
        >
          {/* Card header: title + Edit button */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6" fontWeight={700}>
              {t("briefingsView.campaignInfo", "Campaign info")}
            </Typography>
            {canEditAcInfo && (
              <Tooltip title={t("common.edit", "Edit")}>
                <span>
                  <IconButton
                    onClick={() => setAcInfoDialogOpen(true)}
                    size="small"
                    color="primary"
                    sx={{ bgcolor: "action.hover" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.primary.light + "11",
                  border: `1px solid ${theme.palette.primary.light}44`,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[2] },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  color="primary.main"
                >
                  <WebIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t("briefingsView.platform", "Platform")}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mt: 0.5 }}
                >
                  <Box
                    component="img"
                    src={
                      resolveBriefingPlatformIcon(associatedCampaign.platform)
                        .iconPath
                    }
                    alt={associatedCampaign.platform ?? "platform"}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    {associatedCampaign.platform}
                  </Typography>
                </Stack>
                {associatedCampaign.platform === "NATADS" &&
                  associatedCampaign.nativeAdProvider && (
                    <Typography variant="caption" color="text.secondary">
                      Provider: {associatedCampaign.nativeAdProvider}
                    </Typography>
                  )}
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.secondary.light + "11",
                  border: `1px solid ${theme.palette.secondary.light}44`,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[2] },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  color="secondary.main"
                >
                  <CategoryIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t(
                      "briefingsCreate.form.briefingCampaignType",
                      "Campaign type"
                    )}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                  {campaignType
                    ? t(
                        `briefingsCreate.form.briefingCampaignTypeOptions.${campaignType}`,
                        campaignType
                      )
                    : t("common.notAvailable", "N/A")}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.info.light + "11",
                  border: `1px solid ${theme.palette.info.light}44`,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[2] },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  color="info.main"
                >
                  <LayersIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t("briefingsView.level", "Level")}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                  {associatedCampaign.campaignLevel === "INSERTIONORDER"
                    ? t("briefingsView.insertionOrder", "Insertion order")
                    : t("briefingsView.campaign", "Campaign")}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.warning.light + "11",
                  border: `1px solid ${theme.palette.warning.light}44`,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[2] },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  color="warning.main"
                >
                  <PaymentIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t("briefingsView.investmentPayment", "Investment payment")}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                  {associatedCampaign.investmentPayment === "CLIENTE"
                    ? t("briefingsView.paymentClient", "Client")
                    : associatedCampaign.investmentPayment ===
                        "TI_MEDIA_GROUP_SL"
                      ? t("briefingsView.paymentTiMediaGroupSl", "TI MEDIA")
                      : "-"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("common.name", "Name")}
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                {associatedCampaign.name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("briefingsCreate.form.subcampaignName", "Subcampaign")}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {associatedCampaign.subcampaignName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("briefingsView.sourceId", "Source ID")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 0.5, fontFamily: "monospace" }}
              >
                {associatedCampaign.sourceId || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("briefingsView.addedOn", "Added on")}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {dayjs(associatedCampaign.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("briefingsView.updatedOn", "Last updated")}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {dayjs(associatedCampaign.updatedAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ─── Linked Campaign ─── */}
        {(associatedCampaign as { internalCampaignId?: string | null })
          .internalCampaignId && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${theme.palette.success.light}`,
              p: 3,
              bgcolor: theme.palette.success.light + "11",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <LinkIcon color="success" />
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ fontFamily: "Montserrat" }}
                >
                  {t("briefingsView.linkedCampaign", "Linked campaign")}
                </Typography>
              </Stack>
              <Button
                variant="outlined"
                color="success"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() =>
                  navigate(
                    `/campaigns/${
                      (
                        associatedCampaign as {
                          internalCampaignId?: string | null;
                        }
                      ).internalCampaignId
                    }`
                  )
                }
              >
                {t("briefingsView.viewCampaign", "View campaign")}
              </Button>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, fontFamily: "monospace" }}
            >
              ID:{" "}
              {
                (associatedCampaign as { internalCampaignId?: string | null })
                  .internalCampaignId
              }
            </Typography>
          </Paper>
        )}

        <Stack spacing={3}>
          <AssociatedCampaignGoalSection
            goal={goal}
            onCreateGoal={
              canEditGoal && briefingId && associatedId
                ? handleOpenCreateGoal
                : undefined
            }
            onEditGoal={
              canEditGoal && goal && briefingId && associatedId
                ? handleOpenEditGoal
                : undefined
            }
            onDeleteGoal={
              canEditGoal && goal && briefingId && associatedId
                ? handleOpenDeleteGoal
                : undefined
            }
            isEditMode={canEditGoal}
          />
          <AssociatedCampaignTargetSection
            target={target}
            numberFormatter={numberFormatter}
            onCreateTarget={
              canEditTarget && briefingId && associatedId
                ? handleOpenCreateTarget
                : undefined
            }
            onEditTarget={
              canEditTarget && target && briefingId && associatedId
                ? handleOpenEditTarget
                : undefined
            }
            onDeleteTarget={
              canEditTarget && target && briefingId && associatedId
                ? () => setDeleteTargetModalOpen(true)
                : undefined
            }
            isEditable={canEditTarget && Boolean(briefingId && associatedId)}
          />
          <AssociatedCampaignAssetSection
            asset={asset}
            onCreateAsset={
              canEditAsset && briefingId && associatedId
                ? handleOpenCreateAsset
                : undefined
            }
            onEditAsset={
              canEditAsset && asset && briefingId && associatedId
                ? handleOpenEditAsset
                : undefined
            }
            isEditMode={canEditAsset}
          />
          <AssociatedCampaignSyncSection
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            sync={(associatedCampaign as any).sync ?? null}
            onCreateSync={
              canEditSync && briefingId && associatedId
                ? () => setSyncDialogOpen(true)
                : undefined
            }
            onEditSync={
              canEditSync && briefingId && associatedId
                ? () => setSyncDialogOpen(true)
                : undefined
            }
            isEditMode={canEditSync}
          />
          {canViewMargin && briefingId && associatedId && (
            <Box id="margin" sx={{ scrollMarginTop: 16 }}>
              <AssociatedCampaignMarginSection
                briefingId={briefingId}
                associatedId={associatedId}
                isEditMode={canViewMargin}
                campaignStartDate={goal?.startDate ?? null}
                campaignEndDate={goal?.endDate ?? null}
                goal={
                  goal
                    ? {
                        margin: goal.margin ?? 0,
                        grossUnitCost: goal.grossUnitCost ?? 0,
                        netUnitCost: goal.netUnitCost ?? 0,
                        platformInversion: goal.platformInversion ?? 0,
                        targetVolume: goal.expectedVolume ?? 0,
                        objective: goal.objective ?? "",
                      }
                    : null
                }
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<KeyboardBackspaceIcon />}
              onClick={handleBack}
              variant="text"
            >
              {t("briefingsCreate.back", "Back")}
            </Button>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontFamily: "Montserrat" }}
            >
              {t(
                "briefingsView.associatedCampaignDetails",
                "Associated campaign details"
              )}
            </Typography>
          </Stack>
        </Paper>

        {renderContent()}
      </Box>

      {/* EditAcInfo dialog */}
      {briefingId && associatedId && associatedCampaign && (
        <EditAcInfoDialog
          open={isAcInfoDialogOpen}
          briefingId={briefingId}
          briefing={briefingResponse?.data ?? undefined}
          associatedId={associatedId}
          initialName={associatedCampaign.name ?? ""}
          initialSourceId={associatedCampaign.sourceId ?? ""}
          initialSubcampaignName={associatedCampaign.subcampaignName ?? ""}
          initialCampaignType={associatedCampaign.briefingCampaignType ?? null}
          platform={associatedCampaign.platform}
          campaignLevel={associatedCampaign.campaignLevel}
          companyId={companyId}
          initialNativeAdProvider={associatedCampaign.nativeAdProvider ?? null}
          initialAdvertiserId={associatedCampaign.advertiserId ?? null}
          initialVisibleToClients={associatedCampaign.visibleToClients ?? true}
          onClose={() => setAcInfoDialogOpen(false)}
          onSuccess={refetchAssociatedCampaign}
        />
      )}

      {briefingId && associatedId && (
        <GoalDialog
          open={isGoalDialogOpen}
          onClose={handleCloseGoalDialog}
          briefingId={briefingId}
          associatedId={associatedId}
          initialValues={goalInitialValues}
          mode={goalDialogMode}
          companyName={companyName}
          campaignName={campaignName}
          brandOrProduct={brandOrProduct}
          subcampaignName={associatedCampaign?.subcampaignName ?? undefined}
          isBeApoloCompany={isBeApoloCompany}
          platformType={associatedCampaign?.platform}
          campaignType={campaignType}
          onSuccess={handleGoalActionSuccess}
          briefingTotalBudget={briefingResponse?.data?.totalBudget}
          briefingStatus={briefingResponse?.data?.status}
        />
      )}
      <ConfirmationModal
        open={isDeleteGoalModalOpen}
        title={t(
          "briefingsView.goalDeleteConfirmTitle",
          "Delete goal confirmation"
        )}
        message={t(
          "briefingsView.goalDeleteConfirmMessage",
          "Are you sure you want to delete this goal? This action cannot be undone."
        )}
        onConfirm={handleDeleteGoal}
        onCancel={() => setDeleteGoalModalOpen(false)}
        isLoading={isDeletingGoal}
      />
      {briefingId && associatedId && (
        <ManageTargetDialog
          open={isTargetDialogOpen}
          onClose={() => setTargetDialogOpen(false)}
          briefingId={briefingId}
          associatedId={associatedId}
          mode={targetDialogMode}
          target={target}
          onCompleted={() => {
            setTargetDialogOpen(false);
            refetchAssociatedCampaign();
          }}
        />
      )}
      <ConfirmationModal
        open={isDeleteTargetModalOpen}
        title={t(
          "briefingsView.targetDeleteConfirmTitle",
          "Delete target confirmation"
        )}
        message={t(
          "briefingsView.targetDeleteConfirmMessage",
          "Are you sure you want to delete the audience targeting details?"
        )}
        onConfirm={handleDeleteTarget}
        onCancel={() => setDeleteTargetModalOpen(false)}
        isLoading={isDeletingTarget}
      />
      {briefingId && associatedId && (
        <AssociatedCampaignAssetDialog
          open={isAssetDialogOpen}
          onClose={() => setAssetDialogOpen(false)}
          briefingId={briefingId}
          associatedId={associatedId}
          initialAsset={asset}
          onSuccess={refetchAssociatedCampaign}
        />
      )}

      {briefingId && associatedId && associatedCampaign && (
        <AssociatedCampaignSyncDialog
          open={isSyncDialogOpen}
          onClose={() => setSyncDialogOpen(false)}
          briefingId={briefingId}
          associatedId={associatedId}
          platform={associatedCampaign.platform ?? ""}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialSync={(associatedCampaign as any).sync ?? null}
          onSuccess={() => {
            setSyncDialogOpen(false);
            refetchAssociatedCampaign();
          }}
        />
      )}
    </Box>
  );
};

export default ViewAssociatedCampaign;
